/**
 * ヘッダーのドロップダウン/メガメニュー制御。
 *  - トップレベル項目の開閉（PC: ホバー＋クリック、キーボードfocus、外側クリック/Escで閉じる）
 *  - メガメニュー内の左右切替：左の第2階層タブをクリックすると、右に対応する第3階層パネルを表示
 *    （初期状態は先頭タブがアクティブ）
 *
 *  メガメニューは全幅表示のため絶対配置で、トリガーと本体の間に視覚的な隙間ができる。
 *  そのまま mouseleave で即閉じると隙間通過時に閉じてしまうため、閉じる処理には猶予を設け、
 *  トリガー/メガメニュー本体のいずれかに（再）進入した時点でキャンセルする。
 */
const DESKTOP_MQ = '(min-width: 750px)';
const CLOSE_DELAY = 220;

class HeaderDropdownMenu extends HTMLElement {
  connectedCallback() {
    this._onDocClick = this._onDocClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._timers = new WeakMap();

    this.items = Array.from(this.querySelectorAll('.header-dropdown-menu__item'));

    this.items.forEach((item) => this._bindItem(item));
    this._bindTabs();

    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKeydown);
  }

  _isDesktop() {
    return window.matchMedia(DESKTOP_MQ).matches;
  }

  _getTrigger(item) {
    return item.querySelector(':scope > .header-dropdown-menu__item-link');
  }

  _getRegion(item) {
    return item.querySelector(
      ':scope > .header-mega-menu, :scope > .header-dropdown-menu__item-submenu-wrapper'
    );
  }

  _bindItem(item) {
    const isToggle = item.matches('[data-mega-menu], [data-dropdown-menu]');
    if (!isToggle) return;

    const trigger = this._getTrigger(item);
    if (!trigger) return;

    // クリックで開閉（ボタンのとき）
    if (trigger.tagName === 'BUTTON') {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        const willOpen = !item.classList.contains('is-open');
        this.closeAll();
        if (willOpen) this.open(item);
      });
    }

    // PC: ホバーで開く。離れたら猶予を置いてから閉じる（隙間通過対策）
    item.addEventListener('mouseenter', () => {
      if (!this._isDesktop()) return;
      this._cancelClose(item);
      this.closeAll(item);
      this.open(item);
    });
    item.addEventListener('mouseleave', () => {
      if (!this._isDesktop()) return;
      this._scheduleClose(item);
    });

    // メガメニュー/サブメニュー本体に入ったら閉じるのをキャンセル、出たら再度猶予閉じ
    const region = this._getRegion(item);
    if (region) {
      region.addEventListener('mouseenter', () => this._cancelClose(item));
      region.addEventListener('mouseleave', () => {
        if (this._isDesktop()) this._scheduleClose(item);
      });
    }
  }

  _bindTabs() {
    this.querySelectorAll('[data-mega-panels]').forEach((panels) => {
      const tabs = Array.from(panels.querySelectorAll('[data-mega-tab]'));
      const panelEls = Array.from(panels.querySelectorAll('[data-mega-panel]'));
      if (tabs.length === 0) return;

      const activate = (index) => {
        const idx = String(index);
        tabs.forEach((tab) => {
          const on = tab.dataset.megaTab === idx;
          tab.classList.toggle('is-active', on);
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        panelEls.forEach((panel) => {
          const on = panel.dataset.megaPanel === idx;
          panel.classList.toggle('is-active', on);
          if (on) {
            panel.removeAttribute('hidden');
          } else {
            panel.setAttribute('hidden', '');
          }
        });
      };

      // 第2階層タブはクリックで右パネルを切替（仕様どおりクリック主体）
      tabs.forEach((tab) => {
        tab.addEventListener('click', (event) => {
          event.preventDefault();
          activate(tab.dataset.megaTab);
        });
        // キーボードfocusでも切替（アクセシビリティ）
        tab.addEventListener('focus', () => activate(tab.dataset.megaTab));
      });

      // 初期状態：先頭タブをアクティブに
      activate(tabs[0].dataset.megaTab);
    });
  }

  _scheduleClose(item) {
    this._cancelClose(item);
    const timer = setTimeout(() => this.close(item), CLOSE_DELAY);
    this._timers.set(item, timer);
  }

  _cancelClose(item) {
    const timer = this._timers.get(item);
    if (timer) {
      clearTimeout(timer);
      this._timers.delete(item);
    }
  }

  open(item) {
    this._cancelClose(item);
    item.classList.add('is-open');
    const trigger = this._getTrigger(item);
    if (trigger && trigger.tagName === 'BUTTON') {
      trigger.setAttribute('aria-expanded', 'true');
    }
    const region = this._getRegion(item);
    if (region) region.setAttribute('aria-hidden', 'false');
  }

  close(item) {
    this._cancelClose(item);
    item.classList.remove('is-open');
    const trigger = this._getTrigger(item);
    if (trigger && trigger.tagName === 'BUTTON') {
      trigger.setAttribute('aria-expanded', 'false');
    }
    const region = this._getRegion(item);
    if (region) region.setAttribute('aria-hidden', 'true');
  }

  closeAll(except) {
    this.items.forEach((item) => {
      if (item !== except) this.close(item);
    });
  }

  _onDocClick(event) {
    if (!this.contains(event.target)) this.closeAll();
  }

  _onKeydown(event) {
    if (event.key === 'Escape') this.closeAll();
  }
}

if (!customElements.get('header-dropdown-menu')) {
  customElements.define('header-dropdown-menu', HeaderDropdownMenu);
}
